import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageSeo from '../components/PageSeo';
import bikePricesData from '../data/bikePricesData';
import BikePriceCard from '../components/BikePriceCard';
import './BikePrices.css';

const BRANDS = ['All', 'Hero', 'Honda', 'Bajaj', 'TVS', 'Royal Enfield', 'KTM', 'Yamaha', 'Suzuki', 'Kawasaki'];
const TYPES  = ['All', 'Commuter', 'Street', 'Sport', 'Naked', 'Cruiser', 'Scooter', 'Adventure', 'Sport Tourer'];
const BUDGETS = [
  { label: 'All Budgets', min: 0, max: Infinity },
  { label: 'Under ₹80K',   min: 0,      max: 80000 },
  { label: '₹80K–₹1.2L',  min: 80000,  max: 120000 },
  { label: '₹1.2L–₹2L',   min: 120000, max: 200000 },
  { label: '₹2L–₹3L',     min: 200000, max: 300000 },
  { label: 'Above ₹3L',    min: 300000, max: Infinity },
];

const SORT_OPTIONS = [
  { value: 'default', label: 'Recommended' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
  { value: 'mileage', label: 'Best Mileage' },
  { value: 'engine', label: 'Engine CC' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Which bike is best under 1 lakh in India 2025?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Hero Splendor Plus (₹76,000) and Honda Activa 6G (₹78,000) are the best bikes under 1 lakh in India 2025.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which bike gives best mileage in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Hero Splendor Plus gives 70 kmpl mileage, making it the best mileage bike in India.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which is the best bike under 2 lakh in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Royal Enfield Hunter 350, Bajaj Pulsar NS200, TVS Apache RTR 200 4V are popular choices under 2 lakh.',
      },
    },
  ],
};

export default function BikePrices() {
  const navigate = useNavigate();
  const [search, setSearch]         = useState('');
  const [brandFilter, setBrandFilter] = useState('All');
  const [typeFilter, setTypeFilter]   = useState('All');
  const [budgetFilter, setBudgetFilter] = useState(BUDGETS[0]);
  const [sortBy, setSortBy]           = useState('default');
  const [compareList, setCompareList] = useState([]);
  const [view, setView]               = useState('grid');

  const filteredBikes = useMemo(() => {
    let list = bikePricesData.filter((bike) => {
      const q = search.toLowerCase();
      return (
        (!q || bike.name.toLowerCase().includes(q) || bike.brand.toLowerCase().includes(q)) &&
        (brandFilter === 'All' || bike.brand === brandFilter) &&
        (typeFilter === 'All'  || bike.type  === typeFilter)  &&
        bike.price >= budgetFilter.min && bike.price <= budgetFilter.max
      );
    });
    if (sortBy === 'price_asc')  return [...list].sort((a, b) => a.price   - b.price);
    if (sortBy === 'price_desc') return [...list].sort((a, b) => b.price   - a.price);
    if (sortBy === 'mileage')    return [...list].sort((a, b) => b.mileage - a.mileage);
    if (sortBy === 'engine')     return [...list].sort((a, b) => b.cc      - a.cc);
    return list;
  }, [search, brandFilter, typeFilter, budgetFilter, sortBy]);

  const handleCompare = (id) => {
    setCompareList((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id)
      : prev.length < 2 ? [...prev, id]
      : [prev[1], id]
    );
  };

  const fmt = (n) =>
    '₹' + (n >= 100000
      ? (n / 100000).toFixed(2).replace(/\.?0+$/, '') + 'L'
      : Math.round(n / 1000) + 'K');

  return (
    <>
      <PageSeo
        title="Bike Prices in India 2025 - Compare On Road Prices & EMI"
        description="Check latest bike prices in India 2025. Compare on-road prices, mileage, EMI of Hero, Honda, Bajaj, TVS, Royal Enfield, KTM, Yamaha bikes. Updated monthly."
        keywords="bike prices india 2025, motorcycle price list india, on road bike price, best bike under 1 lakh, hero honda bajaj tvs price"
        path="/bike-prices"
        schema={faqSchema}
      />

      <div className="bike-page">

        {/* ── Hero ── */}
        <section className="bike-hero">
  <div className="bike-shell">

    <p className="bike-breadcrumb">
      MotoHub India › Bike Prices
    </p>

    <h1 className="bike-title">
      Bike Price Index 2025
    </h1>

    <p className="bike-subtitle">
      Compare latest bike prices, mileage,
      EMI and on-road costs across India.
    </p>

    <div className="bike-search">
      <input
        type="text"
        placeholder="Search bike or brand..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>

  </div>
</section>
        {/* ── Sticky filters ── */}
        <section className="bike-filters-wrap">

  <div className="bike-shell">

    <div className="bike-filter-top">

      <div>
        <p className="bike-filter-label">
          Filters
        </p>

        <h3 className="bike-filter-title">
          Showing {filteredBikes.length} bikes
          {search && ` for "${search}"`}
        </h3>
      </div>

      <div className="bike-view-toggle">

        <button
          onClick={() => setView('grid')}
          className={view === 'grid'
            ? 'bike-view-btn active'
            : 'bike-view-btn'}
        >
          ⊞ Grid
        </button>

        <button
          onClick={() => setView('table')}
          className={view === 'table'
            ? 'bike-view-btn active'
            : 'bike-view-btn'}
        >
          ☰ Table
        </button>

      </div>

    </div>

    {/* BRANDS */}
    <div className="bike-filter-group">

      <p className="bike-filter-group-title">
        Brands
      </p>

      <div className="bike-filter-row">

        {BRANDS.map((brand) => (

          <button
            key={brand}
            onClick={() => setBrandFilter(brand)}
            className={
              brandFilter === brand
                ? 'bike-pill bike-pill-active'
                : 'bike-pill'
            }
          >
            {brand}
          </button>

        ))}

      </div>

    </div>

    {/* TYPES */}
    <div className="bike-filter-group">

      <p className="bike-filter-group-title">
        Types
      </p>

      <div className="bike-filter-row">

        {TYPES.map((type) => (

          <button
            key={type}
            onClick={() => setTypeFilter(type)}
            className={
              typeFilter === type
                ? 'bike-pill bike-pill-active'
                : 'bike-pill'
            }
          >
            {type}
          </button>

        ))}

      </div>

    </div>

    {/* BUDGET */}
    <div className="bike-filter-group">

      <p className="bike-filter-group-title">
        Budget
      </p>

      <div className="bike-filter-row">

        {BUDGETS.map((budget) => (

          <button
            key={budget.label}
            onClick={() => setBudgetFilter(budget)}
            className={
              budgetFilter.label === budget.label
                ? 'bike-pill bike-pill-active'
                : 'bike-pill'
            }
          >
            {budget.label}
          </button>

        ))}

      </div>

    </div>

    {/* SORT */}
    <div className="bike-filter-group">

      <p className="bike-filter-group-title">
        Sort By
      </p>

      <div className="bike-filter-row">

        {SORT_OPTIONS.map((option) => (

          <button
            key={option.value}
            onClick={() => setSortBy(option.value)}
            className={
              sortBy === option.value
                ? 'bike-pill bike-pill-active'
                : 'bike-pill'
            }
          >
            {option.label}
          </button>

        ))}

      </div>

    </div>

  </div>

</section>

        {/* ── Main content ── */}
        <div className="max-w-6xl mx-auto px-4 py-8">

          {/* Grid view */}
          {view === 'grid' && (
            <div className="bike-grid">
              {filteredBikes.map((bike) => (
                <BikePriceCard
                  key={bike.id}
                  bike={bike}
                  onCompare={handleCompare}
                  isComparing={compareList.includes(bike.id)}
                />
              ))}
            </div>
          )}

          {/* Table view */}
          {view === 'table' && (
            <div className="overflow-x-auto rounded-xl border border-gray-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900 border-b border-gray-700">
                    {['Bike', 'Brand', 'Type', 'Ex-Showroom', 'On-Road', 'Mileage', 'Engine', ''].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-gray-400 font-medium
                        text-xs uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredBikes.map((bike, i) => (
                    <tr key={bike.id}
                      className={`border-b border-gray-800 hover:bg-gray-900/60 transition
                        ${compareList.includes(bike.id) ? 'bg-orange-500/5'
                          : i % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/30'}`}>
                      <td className="px-4 py-3">
                        <p className="text-white font-medium text-xs">{bike.name}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{bike.brand}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{bike.type}</td>
                      <td className="px-4 py-3 text-orange-400 font-bold text-xs">{fmt(bike.price)}</td>
                      <td className="px-4 py-3 text-gray-300 text-xs">{fmt(bike.onRoad)}</td>
                      <td className="px-4 py-3 text-green-400 text-xs">{bike.mileage} kmpl</td>
                      <td className="px-4 py-3 text-blue-400 text-xs">{bike.cc}cc</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          <button onClick={() => handleCompare(bike.id)}
                            className={`text-xs px-2.5 py-1 rounded-lg transition
                              ${compareList.includes(bike.id)
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>
                            {compareList.includes(bike.id) ? '✓' : '+'}
                          </button>
                          <button onClick={() => navigate('/calculator')}
                            className="bg-orange-500/10 text-orange-400 text-xs
                              px-2.5 py-1 rounded-lg hover:bg-orange-500/20 transition">
                            EMI
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty state */}
          {filteredBikes.length === 0 && (
            <div className="text-center py-16">
              <p className="text-5xl mb-4">🏍️</p>
              <p className="text-white font-bold text-lg">No bikes found</p>
              <p className="text-gray-400 text-sm mt-2">Try different filters</p>
              <button
                onClick={() => {
                  setSearch(''); setBrandFilter('All');
                  setTypeFilter('All'); setBudgetFilter(BUDGETS[0]);
                }}
                className="mt-4 bg-orange-500 text-white px-5 py-2
                  rounded-xl font-bold text-sm">
                Clear Filters
              </button>
            </div>
          )}
        </div>

        <div className="bike-shell bike-bottom-section">

  {/* POPULAR SEARCHES */}
  <section className="bike-premium-box">

    <h2 className="bike-section-title">
      Popular Searches
    </h2>

    <div className="bike-tags">

      {[
        'Best bikes under ₹1 lakh',
        'Best mileage bikes',
        'Royal Enfield bikes',
        'KTM bikes price',
        'Yamaha R15 price',
        'Sports bikes India',
        'Adventure bikes India',
      ].map((item) => (

        <button
          key={item}
          className="bike-tag"
          onClick={() => setSearch(item)}
        >
          {item}
        </button>

      ))}

    </div>

  </section>

  {/* SEO CONTENT */}
  <section className="bike-premium-box bike-seo-content">

    <h2 className="bike-section-title">
      Bike Prices in India 2025 — Complete Guide
    </h2>

    <p>
      Looking for the latest bike prices in India?
      MotoHub India tracks on-road prices of
      motorcycles and scooters across India.
    </p>

    <h3>
      Best Bikes Under ₹1 Lakh
    </h3>

    <p>
      Hero Splendor Plus and Honda Activa 6G
      are among India’s most popular budget bikes
      with excellent mileage and reliability.
    </p>

    <h3>
      Best Bikes ₹1L–₹2L
    </h3>

    <p>
      Bajaj Pulsar, TVS Apache RTR and Yamaha FZ
      dominate this category with sporty looks
      and daily usability.
    </p>

    <h3>
      Premium Bikes Above ₹2L
    </h3>

    <p>
      KTM Duke, Royal Enfield Classic 350 and
      Yamaha R15 V4 remain top enthusiast choices.
    </p>

  </section>

  {/* FAQ */}
  <section className="bike-premium-box">

    <h2 className="bike-section-title">
      Frequently Asked Questions
    </h2>

    <div className="bike-faq">

      {faqSchema.mainEntity.map((faq) => (

        <div
          key={faq.name}
          className="bike-faq-item"
        >

          <h3>
            {faq.name}
          </h3>

          <p>
            {faq.acceptedAnswer.text}
          </p>

        </div>

      ))}

    </div>

  </section>

  {/* INTERNAL LINKS */}
  <section className="bike-premium-box">

    <h2 className="bike-section-title">
      Explore More
    </h2>

    <div className="bike-tags">

      <button
        onClick={() => navigate('/compare')}
        className="bike-tag"
      >
        ⚖️ Compare Bikes
      </button>

      <button
        onClick={() => navigate('/calculator')}
        className="bike-tag"
      >
        💰 EMI Calculator
      </button>

      <button
        onClick={() => navigate('/blogs')}
        className="bike-tag"
      >
        📝 Bike Reviews
      </button>

    </div>

  </section>

</div>

</div>
      {/* ── Floating compare bar ── */}
      {compareList.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50
          bg-orange-500 rounded-2xl px-6 py-3 flex items-center gap-4
          shadow-xl shadow-orange-500/30">
          <p className="text-white text-sm font-medium whitespace-nowrap">
            {compareList.length === 1 ? 'Pick 1 more to compare' : '2 bikes ready'}
          </p>
          {compareList.length === 2 && (
            <button
              onClick={() => navigate(`/compare/${compareList[0]}-vs-${compareList[1]}`)}
              className="bg-white text-orange-500 font-bold px-4 py-1.5
                rounded-xl text-sm whitespace-nowrap">
              Compare Now →
            </button>
          )}
          <button onClick={() => setCompareList([])}
            className="text-white/70 text-lg leading-none">✕</button>
        </div>
      )}
    </>
  );
}